import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  MenuItem,
} from "@mui/material";
import MUIDataTable from "mui-datatables";
import axios from "axios";
import toast from "react-hot-toast";
import AddIcon from "@mui/icons-material/Add";
import Loader from "../../components/loader/Loader";
import { useDispatch } from "react-redux";
import { showConfirmModal, hideConfirmModal } from "../../redux/counterSlice";
import SaveIcon from "@mui/icons-material/Save";
import { grey } from "@mui/material/colors";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import LockResetIcon from "@mui/icons-material/LockReset";
import {
  useTablePagination,
  useTableHeight,
} from "../../hooks/useTablePagination";
import { useSelector } from "react-redux";

const Team = () => {
  const dispatch = useDispatch();

  const [userList, setUserList] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loader, setLoader] = useState(false);
  const [open, setOpen] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const { regionList } = useSelector((state) => state.counter);
  const { shopList } = useSelector((state) => state.counter);
  console.log("region", regionList);

  const {
    page,
    rowsPerPage,
    searchText,
    onChangePage,
    onChangeRowsPerPage,
    onSearchChange,
  } = useTablePagination("team", 10);

  const tableHeight = useTableHeight();

  const [formData, setFormData] = useState({
    user_name: "",
    user_email: "",
    password: "",
    role_id: "",
    region_id: "",
    shop_id: "",
  });

  const [resetPasswordData, setResetPasswordData] = useState({
    user_name: "",
    password: "",
    confirm_password: "",
  });

  // 🔹 Fetch Users
  const loadUsers = async () => {
    try {
      setLoader(true);
      const res = await axios.get("/admin_users");
      setUserList(res?.data?.data || []);
    } catch {
      toast.error("Failed to fetch users");
    } finally {
      setLoader(false);
    }
  };

  // 🔹 Fetch Roles
  const loadRoles = async () => {
    try {
      const res = await axios.get("/roles");
      setRoles(res?.data?.data || []);
    } catch {
      toast.error("Failed to fetch roles");
    }
  };

  useEffect(() => {
    loadUsers();
    loadRoles();
  }, []);

  // 🔹 Get selected role name
  const getSelectedRoleName = () => {
    const selectedRole = roles.find((role) => role.id === formData.role_id);
    return selectedRole?.role_name || "";
  };

  // 🔹 Check if should show region field (for "Dispatch" role)
  const shouldShowRegion = () => {
    const roleName = getSelectedRoleName().toLowerCase();
    return roleName.includes("dispatch");
  };

  // 🔹 Check if should show shop field (for "Shop" role)
  const shouldShowShop = () => {
    const roleName = getSelectedRoleName().toLowerCase();
    return roleName.includes("shop");
  };

  // 🔹 Handle Input Change
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // If role is changed, reset region_id and shop_id
    if (name === "role_id") {
      setFormData({
        ...formData,
        [name]: value,
        region_id: "",
        shop_id: "",
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  // 🔹 Handle Reset Password Input Change
  const handleResetPasswordChange = (e) => {
    setResetPasswordData({
      ...resetPasswordData,
      [e.target.name]: e.target.value,
    });
  };

  // 🔹 Create/Update User
  const handleSubmit = async () => {
    try {
      const payload = {
        user_name: formData.user_name,
        user_email: formData.user_email,
        password: formData.password,
        role_id: formData.role_id,
        region_id: formData.region_id ? formData.region_id : null,
        shop_id: formData.shop_id ? formData.shop_id : null,
      };

      // Check if we're editing (id exists) or creating new user
      if (formData.id) {
        // UPDATE existing user
        await axios.put(`/admin_users/${formData.id}`, payload);
        toast.success("User updated successfully");
      } else {
        // CREATE new user
        await axios.post("/admin_users", payload);
        toast.success("User created successfully");
      }

      setOpen(false);
      loadUsers();

      setFormData({
        user_name: "",
        user_email: "",
        password: "",
        role_id: "",
        region_id: "",
        shop_id: "",
      });
    } catch {
      toast.error(formData.id ? "Update failed" : "User creation failed");
    }
  };

  // 🔹 Reset Password Handler
  const handleResetPassword = async () => {
    try {
      // Validate passwords match
      if (resetPasswordData.password !== resetPasswordData.confirm_password) {
        toast.error("Passwords do not match");
        return;
      }

      // Validate password is not empty
      if (!resetPasswordData.password) {
        toast.error("Password cannot be empty");
        return;
      }

      const payload = {
        user_name: resetPasswordData.user_name,
        password: resetPasswordData.password,
        confirm_password: resetPasswordData.confirm_password,
      };

      await axios.post("/admin/forget-password", payload);
      toast.success("Password reset successfully");

      setResetPasswordOpen(false);
      setResetPasswordData({
        user_name: "",
        password: "",
        confirm_password: "",
      });
    } catch (error) {
      toast.error("Password reset failed");
    }
  };

  // 🔹 Toggle User Status
  const toggleUserStatus = async (user, currentStatus) => {
    try {
      // Get the correct ID field (might be 'id' or 'user_id')
      const userId = user.id || user.user_id;

      if (!userId) {
        toast.error("User ID not found");
        console.error("Cannot find user ID in:", user);
        return;
      }

      // Send complete user data with updated status
      const payload = {
        user_name: user.user_name,
        user_email: user.user_email,
        role_id: user.role_id,
        active_status: currentStatus === 1 ? 0 : 1,
        region_id: user.region_id ? user.region_id : null,
        shop_id: user.shop_id ? user.shop_id : null,
        // Don't send password when just updating status
      };

      await axios.put(`/admin_users/${userId}`, payload);

      toast.success("Status updated successfully");
      loadUsers();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update status");
    } finally {
      // Close the confirm modal using hideConfirmModal
      dispatch(hideConfirmModal({ msg: "" }));
    }
  };

  const editClickHandler = (user) => {
    setFormData({
      id: user.id,
      user_name: user.user_name,
      user_email: user.user_email,
      password: "",
      role_id: user.role_id,
      region_id: user.region_id || "",
      shop_id: user.shop_id || "",
    });
    setOpen(true);
  };

  const resetPasswordClickHandler = (user) => {
    setResetPasswordData({
      user_name: user.user_name,
      password: "",
      confirm_password: "",
    });
    setResetPasswordOpen(true);
  };

  const deleteClickHandler = async (id) => {
    try {
      await axios.delete(`/admin_users/${id}`);

      toast.success("User deleted successfully");
      loadUsers();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error?.response?.data?.message || "Delete failed");
    } finally {
      // Close the confirm modal using hideConfirmModal
      dispatch(hideConfirmModal({ msg: "" }));
    }
  };

  const filteredUserList = userList.filter(
    (user) =>
      user.user_name?.toLowerCase().includes(searchText.toLowerCase()) ||
      user.user_email?.toLowerCase().includes(searchText.toLowerCase()),
  );

  // 🔹 Table Columns
  const columns = [
    {
      name: "id",
      label: "S. No",
      options: {
        customBodyRenderLite: (dataIndex) => dataIndex + 1,
        filter: false,
      },
    },
    { name: "user_name", label: "User Name" },
    {
      name: "role_name",
      label: "Role",
      options: {
        filter: true,
        customBodyRenderLite: (dataIndex) => {
          const user = userList[dataIndex];
          return user?.role?.role_name || "—";
        },
      },
    },
    { name: "user_email", label: "Email" },
    {
      name: "active_status",
      label: "Status",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const user = userList[dataIndex];
          const status = user?.active_status;

          return (
            <Switch
              size="small"
              color={status === 1 ? "success" : "error"}
              checked={status === 1}
              onChange={() =>
                dispatch(
                  showConfirmModal({
                    msg: status === 1 ? "Deactivate User?" : "Activate User?",
                    action: () => toggleUserStatus(user, status),
                  }),
                )
              }
            />
          );
        },
      },
    },
    {
      name: "actions",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        searchable: false,
        customBodyRenderLite: (dataIndex) => {
          const user = userList[dataIndex];
          const status = user?.active_status;

          return (
            <div className="actionBtns">
              <Button
                disabled={status === 1}
                onClick={() => editClickHandler(user)}
              >
                <EditIcon
                  fontSize="medium"
                  sx={{
                    color: status === 1 ? grey[600] : "primary.main",
                  }}
                />
              </Button>
              <Button
                disabled={status === 1}
                onClick={() => resetPasswordClickHandler(user)}
              >
                <LockResetIcon
                  fontSize="medium"
                  sx={{
                    color: status === 1 ? grey[600] : "secondary.main",
                  }}
                />
              </Button>
              <Button
                disabled={status === 1}
                onClick={() =>
                  dispatch(
                    showConfirmModal({
                      msg: "Delete User?",
                      action: () => deleteClickHandler(user.id),
                    }),
                  )
                }
              >
                <DeleteOutlineOutlinedIcon
                  fontSize="medium"
                  sx={{
                    color: status === 1 ? grey[600] : "red",
                  }}
                />
              </Button>
            </div>
          );
        },
      },
    },
  ];

  const options = {
    selectableRows: false,
    filter: false,
    download: false,
    viewColumns: true,
    print: false,
    rowsPerPage: rowsPerPage,
    page: page,
    fixedHeader: true,
    tableBodyHeight: tableHeight,
    onChangePage,
    onChangeRowsPerPage,
    onSearchChange,
    searchText,
    textLabels: {
      body: {
        noMatch: loader ? "Loading..." : "No data available",
      },
    },
  };

  return (
    <>
      {loader ? (
        <Loader />
      ) : (
        <>
          <h4>Admin Management</h4>

          <MUIDataTable
            title={
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  setFormData({
                    user_name: "",
                    user_email: "",
                    password: "",
                    role_id: "",
                    region_id: "",
                    shop_id: "",
                  });
                  setOpen(true);
                }}
              >
                Add User
              </Button>
            }
            data={userList}
            columns={columns}
            options={options}
          />

          {/* 🔹 Create/Edit User Dialog */}
          <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
            <DialogTitle className="text-primary">
              {formData.id ? "Edit User" : "Create User"}
            </DialogTitle>

            <DialogContent>
              <TextField
                fullWidth
                margin="dense"
                label="User Name"
                name="user_name"
                value={formData.user_name}
                onChange={handleChange}
              />

              <TextField
                fullWidth
                margin="dense"
                label="Email"
                name="user_email"
                value={formData.user_email}
                onChange={handleChange}
              />

              <TextField
                fullWidth
                margin="dense"
                type="password"
                label="Password"
                name="password"
                value={formData.password}
                onChange={handleChange}
              />

              {/* ✅ ROLE DROPDOWN */}
              <TextField
                select
                fullWidth
                margin="dense"
                label="Role"
                name="role_id"
                value={formData.role_id}
                onChange={handleChange}
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.role_name}
                  </MenuItem>
                ))}
              </TextField>

              {/* ✅ REGION DROPDOWN - Only show if "Dispatch" role selected */}
              {shouldShowRegion() && (
                <TextField
                  select
                  fullWidth
                  margin="dense"
                  label="Region"
                  name="region_id"
                  value={formData.region_id}
                  onChange={handleChange}
                >
                  {regionList.map((region) => (
                    <MenuItem key={region.id} value={region.region_id}>
                      {region.city?.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {/* ✅ SHOP DROPDOWN - Only show if "Shop" role selected */}
              {shouldShowShop() && (
                <TextField
                  select
                  fullWidth
                  margin="dense"
                  label="Shop"
                  name="shop_id"
                  value={formData.shop_id}
                  onChange={handleChange}
                >
                  {shopList.map((shop) => (
                    <MenuItem key={shop.id} value={shop.shop_details_id}>
                      {shop.shop_name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </DialogContent>

            <DialogActions>
              <Button
                onClick={() => setOpen(false)}
                variant="contained"
                color="error"
              >
                Cancel
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={handleSubmit}
                startIcon={<SaveIcon />}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>

          {/* 🔹 Reset Password Dialog */}
          <Dialog
            open={resetPasswordOpen}
            onClose={() => setResetPasswordOpen(false)}
            fullWidth
          >
            <DialogTitle className="text-primary">Reset Password</DialogTitle>

            <DialogContent>
              <TextField
                fullWidth
                margin="dense"
                label="User Name"
                name="user_name"
                value={resetPasswordData.user_name}
                disabled
              />

              <TextField
                fullWidth
                margin="dense"
                type="password"
                label="New Password"
                name="password"
                value={resetPasswordData.password}
                onChange={handleResetPasswordChange}
              />

              <TextField
                fullWidth
                margin="dense"
                type="password"
                label="Confirm Password"
                name="confirm_password"
                value={resetPasswordData.confirm_password}
                onChange={handleResetPasswordChange}
              />
            </DialogContent>

            <DialogActions>
              <Button
                onClick={() => setResetPasswordOpen(false)}
                variant="contained"
                color="error"
              >
                Cancel
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={handleResetPassword}
                startIcon={<LockResetIcon />}
              >
                Reset Password
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </>
  );
};

export default Team;